/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 */

import { useFormik } from "formik";
import * as Yup from "yup";
import { useState, useEffect } from "react";
import { checkDuplicateMobile, sendOtpData } from "../../core/_requests";
import { useNavigate } from "react-router-dom";
import { initialValuesChefRegister as initialValues } from "../../core/_models";
import { toast } from "react-toastify";
import clsx from "clsx";
import ConfirmationModal from "./ConfirmationModal";

const validationSchema = Yup.object().shape({
  mobileNumber: Yup.string()
    .required("Mobile Number is required")
    .matches(/^\d{10}$/, "Mobile number must be exactly 10 digits")
    .test(
      "no-repeating-digits",
      "Mobile number cannot have repeating 6 consecutive digits",
      (value) => {
        if (!value) return true;
        for (let i = 0; i <= value.length - 6; i++) {
          if (new Set(value.substring(i, i + 6)).size === 1) return false;
        }
        return true;
      }
    ),
});

const Otpdevice = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [btnStatus, setBtnStatus] = useState(true);
  const [modalResponse, setModelResponse] = useState("");
  const [chefData, setChefdata] = useState("");
  const [signupType, setSignupType] = useState("");
  const [chefuuid, setChefuuid] = useState("");

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);

      const chefId = localStorage.getItem("chefuuid");
      const destroyNew = localStorage.getItem("destroyNew");

      const data = {
        type: 2,
        mobileNumber: values.mobileNumber,
        chefId,
        destroyNew,
      };

      try {
        const res = await sendOtpData(data);
        if (res?.status) {
          localStorage.setItem("mobileNumber", values.mobileNumber);
          toast.success(res.message || "OTP sent successfully", { autoClose: 5000 });
          navigate("/otp-verification-mobile");
        } else {
          setShowAlert(true);
          setErrorMessage(res.message);
        }
      } catch (err: any) {
        const errMsg = err?.response?.data?.message;
        setShowAlert(true);
        setErrorMessage(errMsg || "Server error. Please try again.");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleConfirm = async () => {
    localStorage.setItem("destroyNew", "true");
    await formik.submitForm();
  };

  const handleCloseAlert = () => {
    setShowAlert(false);
    setErrorMessage("");
  };

  useEffect(() => {
    const chefStored = localStorage.getItem("chefdata");
    const uuidStored = localStorage.getItem("chefuuid");
    const signupTypeStored = localStorage.getItem("signupType");

    if (uuidStored) setChefuuid(uuidStored);
    if (chefStored) setChefdata(JSON.parse(chefStored).email);
    if (signupTypeStored) setSignupType(signupTypeStored);
  }, []);

  return (
    <div className="container-fluid signup-sec py-3">
      <div className="row">
        <ConfirmationModal
          show={showModal}
          handleClose={() => {
            setBtnStatus(false);
            setShowModal(false);
          }}
          text={modalResponse}
          chefData={chefData}
          handleConfirm={handleConfirm}
        />

        <div className="col-xl-5 d-none d-lg-block">
          <img src="/assets/images/signup-grid.png" className="img-fluid" alt="Signup" />
        </div>

        <div className="col-xl-7">
          <div className="right-form px-4 d-flex flex-column">
            <form onSubmit={formik.handleSubmit} noValidate className="custom-form">
              <div className="content-sec">
                <div className="login-logo mb-4">
                  <img src="/assets/images/logo.svg" alt="Logo" />
                </div>

                {showAlert && (
                  <div className="mb-4 alert alert-danger d-flex justify-content-between align-items-center">
                    <span>{errorMessage}</span>
                    <button type="button" className="btn p-0 ms-3" onClick={handleCloseAlert}>
                      ✕
                    </button>
                  </div>
                )}

                <div className="my-5">
                  <h3 className="fw-bold">Verify Your Mobile Number</h3>
                  <p className="text-muted fw-600">We will send a verification code to your phone.</p>
                </div>

                <div className="col-md-6 mb-4">
                  <label className="fw-600 mb-2">
                    Mobile No <span className="text-danger">*</span>
                  </label>

                  <div className="input-group">
                    <div className="input-group-text">
                      <img src="/assets/images/united-states.png" alt="flag" /> +1
                    </div>

                    <input
                      type="tel"
                      className={clsx("form-control", {
                        "is-invalid": formik.touched.mobileNumber && formik.errors.mobileNumber,
                        "is-valid": formik.touched.mobileNumber && !formik.errors.mobileNumber,
                      })}
                      placeholder="Enter 10 digit number"
                      {...formik.getFieldProps("mobileNumber")}
                      onChange={(e) => {
                        const sanitized = e.target.value.replace(/[^0-9]/g, "");
                        formik.setFieldValue("mobileNumber", sanitized);
                        setBtnStatus(true);

                        if (sanitized.length === 10) {
                          checkDuplicateMobile(sanitized, signupType, chefuuid).then((res) => {
                            if (res?.status) {
                              if (res.isProceed) {
                                setErrorMessage("");
                                setShowAlert(false);
                                setShowModal(false);
                                setModelResponse("");
                                setBtnStatus(false);
                              } else if (res.modal) {
                                setShowModal(true);
                                setModelResponse(res.message);
                                setBtnStatus(true);
                              } else {
                                setErrorMessage(res.message);
                                setShowAlert(true);
                                setBtnStatus(true);
                              }
                            } else {
                              setErrorMessage("");
                              setShowAlert(false);
                              setShowModal(false);
                              setModelResponse("");
                              setBtnStatus(false);
                            }
                          });
                        }
                      }}
                    />
                  </div>

                  {formik.touched.mobileNumber && formik.errors.mobileNumber && (
                    <span className="text-danger">{formik.errors.mobileNumber}</span>
                  )}
                </div>
              </div>

              <div className="row w-100 mt-5">
                <div className="d-flex justify-content-md-end justify-content-center px-0">
                  <button
                    type="submit"
                    disabled={btnStatus || formik.isSubmitting || !formik.isValid || loading}
                    className="btn primary-btn px-5"
                  >
                    {!loading ? "Send OTP" : <span className="spinner-border spinner-border-sm"></span>}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export { Otpdevice };
